// src/components/DailyTeamAttendance.jsx - UPDATED with Admin-style columns
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import DynamicModal from "./DynamicModal";
import EditDailyAttendanceModal from "./EditDailyAttendanceModal";
import {
  getMyDailyAttendance,
  updateDailyAttendance,
} from "../redux/slices/dailyAttendanceSlice";
import { getMyFacilities } from "../redux/slices/facilitySlice";
import DateFilter from "./DateFilter";
import useDateFilter from "./useDateFilter";

const DailyTeamAttendance = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { myAttendanceList, isMyAttendanceLoading, error } = useSelector(
    (state) => state.dailyAttendance,
  );

  const {
    myFacilities = { data: [] },
    isLoading: isLoadingFacilities = false,
  } = useSelector((state) => state.facility || {});

  const [facilities, setFacilities] = useState([]);

  const { dateFilter, updateFilter, getDateRange } = useDateFilter();

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [selectedFacilityId, setSelectedFacilityId] = useState("all");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const buttonRefs = useRef([]);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });

  // Expose getFilterParams method to parent component
  useImperativeHandle(ref, () => ({
    getFilterParams: () => {
      const dateRange = getDateRange();
      const params = {
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
      };

      // Only add facility_id if it's not "all"
      if (selectedFacilityId !== "all") {
        params.facility_id = selectedFacilityId;
      }

      return params;
    },
  }));

  useEffect(() => {
    dispatch(getMyFacilities(1));
  }, [dispatch]);

  // FIXED: For team attendance, we need to use facility_id from the response
  useEffect(() => {
    if (myFacilities.data && myFacilities.data.length > 0) {
      const facilityList = myFacilities.data.map((facility) => ({
        id: facility.facility_id, // This is the actual facility ID (e.g., 3)
        name: facility.facility_name || facility.name,
      }));
      setFacilities(facilityList);
    }
  }, [myFacilities]);

  const showToast = (message, type = "info") => {
    setToast({
      show: true,
      message,
      type,
    });
    setTimeout(() => {
      setToast({ ...toast, show: false });
    }, 3000);
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  useEffect(() => {
    const dateRange = getDateRange();
    const localTimeDifference = -new Date().getTimezoneOffset() / 60;
    const params = {
      from_date: dateRange.from_date,
      to_date: dateRange.to_date,
      local_time_difference: localTimeDifference,
    };

    if (selectedFacilityId !== "all") {
      params.facility_id = selectedFacilityId;
    }

    dispatch(getMyDailyAttendance(params));
  }, [
    dispatch,
    dateFilter.selectedYear,
    dateFilter.viewType,
    dateFilter.selectedQuarter,
    dateFilter.selectedMonth,
    selectedFacilityId,
  ]);

  const handleFacilityFilter = (facilityId) => {
    setSelectedFacilityId(facilityId);
  };

  const handleDropdownToggle = (index, e) => {
    e.stopPropagation();

    if (buttonRefs.current[index]) {
      const rect = buttonRefs.current[index].getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - 120,
      });
    }

    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const handleViewDetails = (rowData) => {
    setSelectedAttendance(rowData);
    setShowViewModal(true);
    setDropdownOpen(null);
  };

  const handleEdit = (rowData) => {
    setSelectedAttendance(rowData);
    setShowEditModal(true);
    setDropdownOpen(null);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedAttendance(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedAttendance(null);
  };

  const handleSaveAttendance = (apiData) => {
    setIsSaving(true);

    dispatch(updateDailyAttendance(apiData))
      .then((action) => {
        setIsSaving(false);

        if (action.payload?.success) {
          showToast("Daily attendance record updated successfully!", "success");
          handleCloseEditModal();
          handleCloseViewModal();

          const dateRange = getDateRange();
          const params = {
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          };

          if (selectedFacilityId !== "all") {
            params.facility_id = selectedFacilityId;
          }

          dispatch(getMyDailyAttendance(params));
        } else {
          showToast(
            action.payload?.message ||
              "Failed to update daily attendance record",
            "error",
          );
        }
      })
      .catch((error) => {
        setIsSaving(false);
        showToast("An error occurred while saving", "error");
      });
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setDropdownOpen(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const transformAttendanceData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      return [];
    }

    return apiData.map((item) => {
      // Parse names from employee_name or split from full name
      let firstName = "N/A";
      let lastName = "N/A";

      if (item.employee_name && item.employee_name !== "N/A") {
        const nameParts = item.employee_name.trim().split(" ");
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(" ");
        } else if (nameParts.length === 1) {
          firstName = nameParts[0];
        }
      }

      // Calculate total hours
      let totalHours = 0;
      if (item.start_time && item.end_time) {
        try {
          const start = new Date(item.start_time);
          const end = new Date(item.end_time);
          totalHours = parseFloat(
            ((end - start) / (1000 * 60 * 60)).toFixed(2),
          );
        } catch (error) {
          totalHours = 0;
        }
      }

      // Find facility name
      const facility = facilities.find((f) => f.id === item.facility_id);

      return {
        id: item.id || "N/A",
        work_date: item.work_date || "N/A",
        last_name: lastName,
        first_name: firstName,
        employeeName: item.employee_name || "N/A",
        job_title: item.job_title || "Dietitian",
        facility_name:
          facility?.name || item.facility_name || "Unknown Facility",
        facility_id: item.facility_id,
        building_group: item.customer_group_name || "N/A",
        clock_in:
          item.clock_in !== "N/A"
            ? new Date(`2000-01-01T${item.clock_in}`).toLocaleTimeString(
                "en-US",
                {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                },
              )
            : "N/A",
        clock_out:
          item.clock_out !== "N/A"
            ? new Date(`2000-01-01T${item.clock_out}`).toLocaleTimeString(
                "en-US",
                {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                },
              )
            : "N/A",
        total_hours: item.total_hours,
        originalData: item,
      };
    });
  };

  const attendanceData = isMyAttendanceLoading
    ? []
    : transformAttendanceData(myAttendanceList);

  const filteredData =
    selectedFacilityId === "all"
      ? attendanceData
      : attendanceData.filter(
          (attendance) =>
            attendance.facility_id === parseInt(selectedFacilityId, 10),
        );

  const facilityOptions = [
    { id: "all", name: "All Facilities" },
    ...facilities.map((f) => ({ id: f.id, name: f.name })),
  ];

  const columns = [
    {
      name: "work_date",
      label: "Work Date",
      options: {
        sort: false,
        customBodyRender: (value) => {
          return (
            <div style={{ fontWeight: "500", color: "#1A1A1A" }}>{value}</div>
          );
        },
      },
    },
    {
      name: "last_name",
      label: "Last Name",
      options: {
        sort: false,
      },
    },
    {
      name: "first_name",
      label: "First Name",
      options: {
        sort: false,
      },
    },
    {
      name: "job_title",
      label: "Job Title",
      options: {
        sort: false,
      },
    },
    {
      name: "facility_name",
      label: "Facility Name",
      options: {
        sort: false,
        customBodyRender: (value) => {
          return (
            <div
              style={{
                padding: "4px 8px",
                backgroundColor: "#E5F4FF",
                color: "#1976D2",
                borderRadius: "12px",
                fontSize: "0.85rem",
                fontWeight: 500,
                display: "inline-block",
              }}>
              {value}
            </div>
          );
        },
      },
    },
    {
      name: "building_group",
      label: "Building Group",
      options: {
        sort: false,
        customBodyRender: (value) => {
          if (!value || value === "N/A") {
            return <span style={{ color: "#999" }}>—</span>;
          }
          return (
            <div
              style={{
                padding: "4px 8px",
                backgroundColor: "#F3E5F5",
                color: "#7B1FA2",
                borderRadius: "12px",
                fontSize: "0.85rem",
                fontWeight: 500,
                display: "inline-block",
              }}>
              {value}
            </div>
          );
        },
      },
    },
    {
      name: "clock_in",
      label: "Clock In",
      options: {
        sort: false,
        customBodyRender: (value) => {
          return (
            <div style={{ color: "#333" }}>{value !== "N/A" ? value : "—"}</div>
          );
        },
      },
    },
    {
      name: "clock_out",
      label: "Clock Out",
      options: {
        sort: false,
        customBodyRender: (value) => {
          return (
            <div style={{ color: "#333" }}>{value !== "N/A" ? value : "—"}</div>
          );
        },
      },
    },
    {
      name: "total_hours",
      label: "Total Hours",
      options: {
        sort: false,
        customBodyRender: (value) => {
          let color = "#666";
          let displayValue = value;

          if (value !== 0 && value !== "N/A") {
            if (value < 4) color = "#D32F2F";
            else if (value < 8) color = "#FF8104";
            else color = "#29BF5A";
            displayValue = `${value} hours`;
          } else if (value === 0) {
            displayValue = "0 hours";
          }

          return <div style={{ color, fontWeight: "500" }}>{displayValue}</div>;
        },
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                ref={(el) => (buttonRefs.current[dataIndex] = el)}
                onClick={(e) => handleDropdownToggle(dataIndex, e)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                }}>
                <Icon icon="mdi:dots-horizontal" width="25" height="25" />
              </button>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    searchPlaceholder: "Search by name, facility, job title...",
    pagination: false,
    tableBodyHeight: "auto",
    textLabels: {
      body: {
        noMatch: isMyAttendanceLoading
          ? "Loading..."
          : "No daily attendance records found for selected period",
      },
    },
  };

  const ViewAttendanceModal = ({ attendance, onClose, onEdit }) => {
    if (!attendance) return null;

    return (
      <div>
        <div
          style={{
            background: "#F7F8FA",
            padding: "20px",
            borderRadius: "16px",
            marginBottom: "20px",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}>
          <div style={{ marginBottom: "15px" }}>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Work Date:</strong> {attendance.work_date}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Last Name:</strong> {attendance.last_name}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>First Name:</strong> {attendance.first_name}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Job Title:</strong> {attendance.job_title}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Facility Name:</strong> {attendance.facility_name}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Building Group:</strong>{" "}
              {attendance.building_group !== "N/A"
                ? attendance.building_group
                : "—"}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Clock In:</strong>{" "}
              {attendance.clock_in !== "N/A" ? attendance.clock_in : "—"}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Clock Out:</strong>{" "}
              {attendance.clock_out !== "N/A" ? attendance.clock_out : "—"}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Total Hours:</strong> {attendance.total_hours} hours
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="mb-4 pb-2 border-bottom">
        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          <div style={{ minWidth: "300px" }}>
            <DateFilter
              {...dateFilter}
              onFilterChange={updateFilter}
              size="sm"
            />
          </div>

          <div style={{ minWidth: "200px" }}>
            <select
              className="form-select form-select-sm"
              value={selectedFacilityId}
              onChange={(e) => handleFacilityFilter(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
                backgroundColor: "white",
                fontSize: "14px",
              }}>
              {facilityOptions.map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
          <span className="badge bg-light text-dark p-2">
            <i className="fas fa-calendar-alt me-1"></i>
            {getDateRange().label}
          </span>

          {selectedFacilityId !== "all" && (
            <span className="badge bg-info text-white p-2">
              <i className="fas fa-building me-1"></i>
              Facility:{" "}
              {
                facilityOptions.find(
                  (f) => f.id === parseInt(selectedFacilityId, 10),
                )?.name
              }
              <button
                onClick={() => setSelectedFacilityId("all")}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  marginLeft: "8px",
                  padding: "0 4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}>
                ×
              </button>
            </span>
          )}

          {!isMyAttendanceLoading &&
            myAttendanceList &&
            myAttendanceList.length > 0 && (
              <span className="badge bg-success p-2">
                <i className="fas fa-clock me-1"></i>
                {myAttendanceList.length} Records
              </span>
            )}
        </div>
      </div>

      {isMyAttendanceLoading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: "10px" }}>
            Loading your daily attendance data...
          </p>
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#D32F2F" }}>
          <Icon icon="material-symbols:error-outline" width="48" height="48" />
          <p style={{ marginTop: "10px" }}>Error: {error}</p>
          <button
            onClick={() => {
              const dateRange = getDateRange();
              const params = {
                from_date: dateRange.from_date,
                to_date: dateRange.to_date,
              };

              if (selectedFacilityId !== "all") {
                params.facility_id = selectedFacilityId;
              }

              dispatch(getMyDailyAttendance(params));
            }}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              backgroundColor: "#8B2885",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}>
            Retry
          </button>
        </div>
      ) : (
        <>
          {attendanceData.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              <Icon icon="mdi:calendar-clock" width="48" height="48" />
              <p style={{ marginTop: "10px" }}>
                No daily attendance records found for {getDateRange().label}
                {selectedFacilityId !== "all" &&
                  ` at ${facilityOptions.find((f) => f.id === parseInt(selectedFacilityId, 10))?.name}`}
                .
              </p>
              <p style={{ fontSize: "14px", marginTop: "8px", color: "#999" }}>
                Try selecting a different date range or facility
              </p>
            </div>
          ) : (
            <div className="basic-data-table">
              <MUIDataTable
                title=""
                data={filteredData}
                columns={columns}
                options={options}
                className="overflow-hidden packageTable"
              />
            </div>
          )}
        </>
      )}

      <DynamicModal
        show={showViewModal}
        handleClose={handleCloseViewModal}
        title="Daily Attendance Details"
        modalWidth="500px"
        content={
          <ViewAttendanceModal
            attendance={selectedAttendance}
            onClose={handleCloseViewModal}
            onEdit={(attendance) => {
              setSelectedAttendance(attendance);
              setShowEditModal(true);
            }}
          />
        }
      />

      <DynamicModal
        show={showEditModal}
        handleClose={handleCloseEditModal}
        title={`Edit Daily Attendance - ${selectedAttendance?.employeeName || ""}`}
        modalWidth="500px"
        content={
          <EditDailyAttendanceModal
            attendanceData={selectedAttendance}
            isEditMode={true}
            onClose={handleCloseEditModal}
            onSave={handleSaveAttendance}
            isSaving={isSaving}
            facilities={facilities}
            isLoadingFacilities={isLoadingFacilities}
          />
        }
      />

      {dropdownOpen !== null &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: buttonPosition.top,
              left: buttonPosition.left,
              backgroundColor: "white",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              minWidth: "140px",
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#333",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() => handleViewDetails(filteredData[dropdownOpen])}>
              <Icon
                icon="mdi:eye-outline"
                width="18"
                height="18"
                color="#2196F3"
              />
              <span>View Details</span>
            </div>
            <div
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#333",
                borderTop: "1px solid #F0F0F0",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() => handleEdit(filteredData[dropdownOpen])}>
              <Icon icon="line-md:edit" width="18" height="18" color="#666" />
              <span>Edit</span>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
});

// Set display name for debugging
DailyTeamAttendance.displayName = "DailyTeamAttendance";

export default DailyTeamAttendance;
